import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { ShoppingCart, Users, Package } from 'lucide-react';
import { motion } from 'motion/react';
import { type ColumnDef } from '@tanstack/react-table';
import { apiClient } from '../api/client';
import SplitfinTable from './shared/SplitfinTable';
import CompactAISummary from './CompactAISummary';
import { RevenueChart, OrdersChart, StockChart, AgentChart } from './dashboard/DashboardCharts';

interface TopAgent {
  id?: string;
  name: string;
  orderCount: number;
  revenue: number;
}

interface DashboardMetrics {
  ordersCount: number;
  ordersRevenue: number;
  stockTotal: number;
  topAgent: TopAgent;
  pendingOrders: number;
  newCustomers: number;
  lowStockItems: number;
  avgOrderValue: number;
  orderCountChartData: Array<{ name: string; value: number }>;
  orderRevenueChartData: Array<{ name: string; value: number }>;
  stockChartData: Array<{ name: string; value: number }>;
  topAgentChartData: Array<{ name: string; value: number }>;
}

type DateRange = '7_days' | '30_days' | '90_days' | 'this_year' | 'all_time';

const DATE_RANGE_OPTIONS: { value: DateRange; label: string }[] = [
  { value: '7_days', label: 'Last 7 Days' },
  { value: '30_days', label: 'Last 30 Days' },
  { value: '90_days', label: 'Last 90 Days' },
  { value: 'this_year', label: 'This Year' },
  { value: 'all_time', label: 'All Time' },
];

interface RecentOrder {
  id: number;
  orderNumber: string;
  referenceNumber: string | null;
  customer: string;
  date: string;
  status: string;
  total: number;
}

interface RecentProduct {
  id: number;
  sku: string;
  name: string;
  brand: string;
  price: number;
  stock: number;
  imageUrl: string | null;
  addedAt: string;
}

interface RecentCustomer {
  id: number;
  companyName: string;
  contactName: string;
  email: string;
  status: string;
  totalSpent: number;
  orderCount: number;
  addedAt: string;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    draft: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
    confirmed: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
    pending: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
    shipped: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
    invoiced: 'bg-purple-500/20 text-purple-400 border border-purple-500/30',
    delivered: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
    fulfilled: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
    active: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
    inactive: 'bg-destructive/20 text-destructive border border-destructive/30',
  };
  return colors[status] || 'bg-muted-foreground/10 text-muted-foreground border border-muted-foreground/20';
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`px-2.5 py-0.5 rounded-md text-xs font-medium capitalize ${getStatusColor(status)}`}>
    {status}
  </span>
);

const DashboardContent: React.FC = () => {
  usePageTitle('Dashboard');
  const [dateRange, setDateRange] = useState<DateRange>('30_days');
  const [showOneDriveConnected, setShowOneDriveConnected] = useState(false);
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    ordersCount: 0,
    ordersRevenue: 0,
    stockTotal: 0,
    topAgent: { name: 'N/A', orderCount: 0, revenue: 0 },
    pendingOrders: 0,
    newCustomers: 0,
    lowStockItems: 0,
    avgOrderValue: 0,
    orderCountChartData: [],
    orderRevenueChartData: [],
    stockChartData: [],
    topAgentChartData: [],
  });
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [recentProducts, setRecentProducts] = useState<RecentProduct[]>([]);
  const [recentCustomers, setRecentCustomers] = useState<RecentCustomer[]>([]);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [tablesLoading, setTablesLoading] = useState(true);
  const hasLoadedRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    loadDashboardData();
  }, [dateRange]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('onedrive') === 'connected') {
      setShowOneDriveConnected(true);
      params.delete('onedrive');
      const cleaned = params.toString();
      const nextUrl = cleaned ? `${location.pathname}?${cleaned}` : location.pathname;
      navigate(nextUrl, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const loadDashboardData = async () => {
    const isInitialLoad = !hasLoadedRef.current;
    try {
      setMetricsLoading(true);
      const metricsResponse = await apiClient.get(`/analytics/dashboard?date_range=${dateRange}`);
      setMetrics(metricsResponse.data);
      hasLoadedRef.current = true;

      if (isInitialLoad) {
        const [ordersRes, productsRes, customersRes] = await Promise.all([
          apiClient.get('/analytics/recent-orders?limit=5'),
          apiClient.get('/analytics/recent-products?limit=5'),
          apiClient.get('/analytics/recent-customers?limit=5'),
        ]);

        setRecentOrders(ordersRes.data);
        setRecentProducts(productsRes.data);
        setRecentCustomers(customersRes.data);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setMetricsLoading(false);
      setTablesLoading(false);
    }
  };

  const orderColumns = useMemo<ColumnDef<RecentOrder>[]>(() => [
    {
      accessorKey: 'orderNumber',
      header: 'Order #',
      size: 160,
      cell: ({ row }) => (
        <div className="min-w-[var(--table-id-min-width)]">
          <div className="truncate font-medium" title={row.original.orderNumber}>{row.original.orderNumber}</div>
          {row.original.referenceNumber && row.original.referenceNumber.trim() !== row.original.orderNumber.trim() && (
            <div className="text-[11px] text-muted-foreground">{row.original.referenceNumber}</div>
          )}
        </div>
      ),
    },
    { accessorKey: 'customer', header: 'Customer', size: 180 },
    {
      accessorKey: 'date',
      header: 'Date',
      size: 100,
      cell: ({ row }) => <span className="text-muted-foreground">{formatDate(row.original.date)}</span>,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 100,
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'total',
      header: () => <div className="text-right">Total</div>,
      size: 90,
      cell: ({ row }) => (
        <span className="block text-right font-medium tabular-nums">{formatCurrency(row.original.total)}</span>
      ),
    },
  ], []);

  const customerColumns = useMemo<ColumnDef<RecentCustomer>[]>(() => [
    {
      accessorKey: 'companyName',
      header: 'Customer',
      size: 240,
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="truncate font-medium">{row.original.companyName}</div>
          <div className="truncate text-xs text-muted-foreground">{row.original.email || 'No email'}</div>
        </div>
      ),
    },
    {
      accessorKey: 'orderCount',
      header: () => <div className="text-right">Orders</div>,
      size: 70,
      cell: ({ row }) => (
        <span className="block text-right tabular-nums text-muted-foreground">{row.original.orderCount}</span>
      ),
    },
    {
      accessorKey: 'totalSpent',
      header: () => <div className="text-right">Spent</div>,
      size: 90,
      cell: ({ row }) => (
        <span className="block text-right font-medium tabular-nums">{formatCurrency(row.original.totalSpent)}</span>
      ),
    },
  ], []);

  const productColumns = useMemo<ColumnDef<RecentProduct>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Product',
      size: 240,
      cell: ({ row }) => (
        <div className="flex items-center gap-2.5">
          {row.original.imageUrl ? (
            <img
              src={row.original.imageUrl}
              alt=""
              className="w-7 h-7 rounded object-cover bg-muted shrink-0"
            />
          ) : (
            <div className="w-7 h-7 rounded bg-muted/60 shrink-0 flex items-center justify-center">
              <Package size={14} className="text-muted-foreground/40" />
            </div>
          )}
          <div className="min-w-0">
            <div className="truncate font-medium">{row.original.name}</div>
            <div className="text-[11px] text-muted-foreground">{row.original.sku}</div>
          </div>
        </div>
      ),
    },
    { accessorKey: 'brand', header: 'Brand', size: 120 },
    {
      accessorKey: 'price',
      header: 'Price',
      size: 90,
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">{formatCurrency(row.original.price)}</span>
      ),
    },
    {
      accessorKey: 'stock',
      header: 'Stock',
      size: 70,
      cell: ({ row }) => <span className="tabular-nums">{row.original.stock}</span>,
    },
  ], []);

  return (
    <div className="mx-auto max-w-[var(--dashboard-content-max)] px-4 py-6 sm:px-6">
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        className="mb-6"
      >
        {showOneDriveConnected && (
          <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-emerald-200 text-sm flex items-center justify-between gap-4">
            <div>
              <div className="font-medium text-emerald-100">OneDrive connected</div>
              <div className="text-emerald-200/80">Your OneDrive is now linked to Splitfin.</div>
            </div>
            <button
              className="text-emerald-200/80 hover:text-emerald-100"
              onClick={() => setShowOneDriveConnected(false)}
            >
              Dismiss
            </button>
          </div>
        )}
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">Dashboard</h1>
            <p className="mt-1 text-sm text-muted-foreground">Performance metrics follow selected reporting period. Inventory and latest activity stay current.</p>
          </div>
          <div>
            <p id="reporting-period-label" className="mb-1.5 text-xs font-medium text-muted-foreground">Reporting period</p>
            <div className="flex flex-wrap items-center gap-0.5 rounded-[var(--radius-control)] border border-[var(--border-subtle)] bg-[var(--surface-interactive)] p-0.5" role="group" aria-labelledby="reporting-period-label">
              {DATE_RANGE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setDateRange(option.value)}
                  aria-pressed={dateRange === option.value}
                  className={`rounded-[var(--radius-control)] px-3 py-1.5 text-xs font-medium transition-colors duration-[var(--motion-fast)] ease-[var(--ease-out-standard)] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)] ${dateRange === option.value
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-background'
                    }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-4 max-w-3xl"><CompactAISummary companyId="dm-brands" /></div>
      </motion.div>

      <section aria-labelledby="performance-heading" className="mb-8">
        <div className="mb-3 flex items-center justify-between"><h2 id="performance-heading" className="text-base font-semibold">Performance</h2>{metricsLoading && <span className="text-xs text-muted-foreground" aria-live="polite">Updating metrics…</span>}</div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(var(--dashboard-card-min-width),1fr))] gap-4">
        <motion.div className="h-full" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: 'easeOut', delay: 0 }}>
          <RevenueChart
            data={metrics.orderRevenueChartData}
            total={metrics.ordersRevenue}
            onClick={() => navigate('/orders')}
          />
        </motion.div>

        <motion.div className="h-full" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: 'easeOut', delay: 0.05 }}>
          <OrdersChart
            data={metrics.orderCountChartData}
            total={metrics.ordersCount}
            onClick={() => navigate('/orders')}
          />
        </motion.div>

        <motion.div className="h-full" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: 'easeOut', delay: 0.1 }}>
          <StockChart
            data={metrics.stockChartData}
            total={metrics.stockTotal}
            onClick={() => navigate('/inventory/products')}
          />
        </motion.div>

        <motion.div className="h-full" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: 'easeOut', delay: 0.15 }}>
          <AgentChart
            data={metrics.topAgentChartData}
            agentName={metrics.topAgent.name}
            orderCount={metrics.topAgent.orderCount}
            revenue={metrics.topAgent.revenue}
            onClick={() => navigate('/agents')}
          />
        </motion.div>
      </div>
      </section>

      <section aria-labelledby="latest-activity-heading" className="mb-8">
        <h2 id="latest-activity-heading" className="mb-3 text-base font-semibold">Latest activity</h2>
      <div className="grid gap-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(var(--dashboard-table-min-width), 1fr))' }}>
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: 'easeOut', delay: 0.2 }}>
          <SplitfinTable
            title="Latest Orders"
            variant="dashboard-preview"
            viewAllLink={{ label: 'View orders', onClick: () => navigate('/orders') }}
            columns={orderColumns}
            data={recentOrders}
            getRowHref={(item) => `/order/${item.id}`}
            onRowClick={(item) => navigate(`/order/${item.id}`)}
            loading={tablesLoading}
            emptyState={{
              icon: <ShoppingCart size={32} />,
              title: 'No orders yet',
              description: 'Orders will appear here once created',
            }}
          />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: 'easeOut', delay: 0.25 }}>
          <SplitfinTable
            title="New Customers"
            variant="dashboard-preview"
            viewAllLink={{ label: 'View customers', onClick: () => navigate('/customers') }}
            columns={customerColumns}
            data={recentCustomers}
            getRowHref={(item) => `/customers/${item.id}`}
            onRowClick={(item) => navigate(`/customers/${item.id}`)}
            loading={tablesLoading}
            emptyState={{
              icon: <Users size={32} />,
              title: 'No customers yet',
              description: 'Customers will appear here once added',
            }}
          />
        </motion.div>
      </div>
      </section>

      <section aria-labelledby="product-activity-heading">
        <h2 id="product-activity-heading" className="mb-3 text-base font-semibold">Product activity</h2>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: 'easeOut', delay: 0.3 }}>
        <SplitfinTable
          className="mb-6"
          title="Recently Added Products"
          variant="dashboard-preview"
          viewAllLink={{ label: 'View products', onClick: () => navigate('/inventory/products') }}
          columns={productColumns}
          data={recentProducts}
          getRowHref={(item) => `/inventory/products/?page=1&status=active&search=${item.sku}`}
          onRowClick={(item) => navigate(`/inventory/products/?page=1&status=active&search=${item.sku}`)}
          loading={tablesLoading}
          emptyState={{
            icon: <Package size={32} />,
            title: 'No products yet',
            description: 'Products will appear here once added',
          }}
        />
      </motion.div>
      </section>
    </div>
  );
};

export default DashboardContent;
